import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatSidenav, MatSidenavContent } from '@angular/material/sidenav';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { IconModule } from 'src/app/icon/icon.module';
import { MaterialModule } from 'src/app/material.module';
import { CoreService } from 'src/app/services/core.service';
import { InformationService } from 'src/app/services/information.service';
import { ROUTES } from 'src/app/shared/constants';
import { BreadcrumbComponent } from 'src/app/shared/components/breadcrumb/breadcrumb.component';
import { HeaderComponent } from './vertical/header/header.component';
import { AppNavItemComponent } from './vertical/sidebar/nav-item/nav-item.component';
import { navItems } from './vertical/sidebar/sidebar-data';
import { SidebarComponent } from './vertical/sidebar/sidebar.component';
import { TranslateModule } from '@ngx-translate/core';

const MOBILE_VIEW = 'screen and (max-width: 768px)';
const TABLET_VIEW = 'screen and (min-width: 769px) and (max-width: 1024px)';
const MONITOR_VIEW = 'screen and (min-width: 1024px)';
const BELOWMONITOR = 'screen and (max-width: 1023px)';

// for mobile app sidebar
interface apps {
  id: number;
  img: string;
  title: string;
  subtitle: string;
  link: string;
}

interface quicklinks {
  id: number;
  title: string;
  link: string;
}

@Component({
  imports: [
    RouterModule,
    AppNavItemComponent,
    MaterialModule,
    CommonModule,
    SidebarComponent,
    NgScrollbarModule,
    IconModule,
    HeaderComponent,
    BreadcrumbComponent,
    TranslateModule
  ],
  encapsulation: ViewEncapsulation.None,
  selector: 'app-main-layout',
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss'
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  navItems = navItems.filter((item) => item.route !== ROUTES.USER.INFORMATION);
  private informationSubscription = Subscription.EMPTY;
  @ViewChild('leftSidenav') public sidenav: MatSidenav;
  resView = false;
  @ViewChild('content', { static: true }) content!: MatSidenavContent;
  //get options from service
  options = this.settings.getOptions();
  public isContentWidthFixed = true;
  public isCollapsedWidthFixed = false;
  private layoutChangesSubscription = Subscription.EMPTY;
  private isMobileScreen = false;
  public sideNavState = false;
  readonly currentYear = new Date().getFullYear();
  private readonly htmlElement = document.documentElement;

  get isOver(): boolean {
    return this.isMobileScreen;
  }

  get isToggle(): boolean {
    return this.options.sidenavCollapsed;
  }

  get isTablet(): boolean {
    return this.resView;
  }

  // for mobile app sidebar
  apps: apps[] = [];

  quicklinks: quicklinks[] = [];

  constructor(
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private cdr: ChangeDetectorRef,
    private settings: CoreService,
    private informationService: InformationService) {
      this.applyDashboardTheme();
      this.layoutChangesSubscription = this.breakpointObserver
                                           .observe([MOBILE_VIEW, TABLET_VIEW, MONITOR_VIEW, BELOWMONITOR])
                                           .subscribe((state) => {
          this.options.sidenavOpened = true;
          this.isMobileScreen = state.breakpoints[BELOWMONITOR];
          if (this.options.sidenavCollapsed == false) {
            this.options.sidenavCollapsed = state.breakpoints[TABLET_VIEW];
          }
          this.isContentWidthFixed = state.breakpoints[MONITOR_VIEW];
          this.resView = state.breakpoints[BELOWMONITOR];
          // this.cdr.detectChanges();
      });

    // This is for scroll to top
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.content.scrollTo({ top: 0 });
    });
  }

  isFilterNavOpen = false;

  toggleFilterNav() {
    this.isFilterNavOpen = !this.isFilterNavOpen;
    this.cdr.detectChanges(); // Ensures Angular updates the view
  }

  ngOnInit() {
    this.informationSubscription = this.informationService.getInformations({ page: 1, pageSize: 1 }).subscribe({
      next: (response) => {
        const hasInformation = (response?.totalRecord ?? response?.data?.length ?? 0) > 0;
        if (hasInformation) this.navItems = navItems;
      },
      error: () => {}
    });
  }

  ngOnDestroy() {
    this.layoutChangesSubscription.unsubscribe();
    this.informationSubscription.unsubscribe();
    this.useLightTheme();
  }

  toggleCollapsed() {
    this.isContentWidthFixed = false;
    this.options.sidenavCollapsed = !this.options.sidenavCollapsed;
    this.resetCollapsedState();
  }

  resetCollapsedState(timer = 400) {
    setTimeout(() => this.settings.setOptions(this.options), timer);
  }

  onSidenavClosedStart() {
    this.isContentWidthFixed = false;
  }

  onSidenavOpenedChange(isOpened: boolean) {
    this.isCollapsedWidthFixed = !this.isOver;
    this.options.sidenavOpened = isOpened;
    this.settings.setOptions(this.options);
  }

  stateChanged(state: boolean) {
    if (this.options.sidenavCollapsed) {
      this.sideNavState = state;
    }
  }

  get sideNavClass(): string {
    if (!this.options.sidenavCollapsed) return '';
    return 'overflow-hidden w-full! p-0! pl-1.5! [&_img]:mx-0 small-logo';
  }

  private applyDashboardTheme() {
    const isDark = this.options.theme === 'dark';
    this.htmlElement.classList.toggle('dark-theme', isDark);
    this.htmlElement.classList.toggle('light-theme', !isDark);
  }

  private useLightTheme() {
    this.htmlElement.classList.remove('dark-theme');
    this.htmlElement.classList.add('light-theme');
  }
}
